import {
  isProductoExtra,
  normalizarNombreCategoria,
} from "./productoMapper";

/**
 * Mapper de categorías — Portal Cliente Menuee
 *
 * Responsabilidad: transformar filas de categorias_producto a objetos de vista
 * (barra de categorías, agrupación de carta).
 *
 * Consume: filas crudas devueltas por productosService (PostgREST).
 *
 * Qué nunca debe hacer:
 * - Consultar Supabase directamente.
 * - Filtrar productos ni calcular totales.
 * - Contener lógica visual ni JSX.
 *
 * Flujo: BarraCategorias ← mapCategoriaToView() ← productosService ← Supabase
 */

/**
 * @typedef {Object} CategoriaView
 * @property {string} id
 * @property {string} nombre
 * @property {number} orden
 * @property {string} [codigo]
 * @property {boolean} activo
 */

/**
 * Mapea una fila de categorias_producto a CategoriaView.
 *
 * @param {Object|null|undefined} row
 * @returns {CategoriaView|null}
 */
export function mapCategoriaToView(row) {
  if (!row || row.activo === false) {
    return null;
  }

  const nombre = String(row.nombre || "").trim();
  if (!nombre) {
    return null;
  }

  return {
    id: row.id,
    nombre,
    orden: Number.isFinite(Number(row.orden)) ? Number(row.orden) : 0,
    codigo: row.codigo ?? null,
    activo: true,
  };
}

/**
 * @param {Object[]} rows
 * @returns {CategoriaView[]}
 */
export function mapCategoriasToView(rows) {
  return (rows || []).map(mapCategoriaToView).filter(Boolean);
}

/**
 * Conserva el orden de categorias_producto y deja solo las que tienen productos visibles.
 *
 * @param {CategoriaView[]} categorias
 * @param {import('./productoMapper').ProductoLegacy[]} productos
 * @returns {CategoriaView[]}
 */
export function filterCategoriasConProductos(categorias, productos) {
  const idsConProductos = new Set(
    (productos || []).map((p) => p.categoriaId).filter(Boolean)
  );

  return (categorias || []).filter((c) => idsConProductos.has(c.id));
}

/**
 * @param {CategoriaView|null|undefined} categoria
 * @returns {boolean}
 */
export function isCategoriaExtraView(categoria) {
  if (!categoria) {
    return false;
  }
  if (categoria.es_extra === true) {
    return true;
  }
  return normalizarNombreCategoria(categoria.nombre) === "extras";
}

/**
 * Categorías visibles en la barra, excluyendo "Extras" y sin productos de carta.
 *
 * @param {CategoriaView[]} categorias
 * @param {import('./productoMapper').ProductoLegacy[]} productos
 * @returns {CategoriaView[]}
 */
export function filterCategoriasParaCarta(categorias, productos) {
  const productosCarta = (productos || []).filter((p) => !isProductoExtra(p));
  return filterCategoriasConProductos(categorias, productosCarta).filter(
    (c) => !isCategoriaExtraView(c)
  );
}
